import React, { useEffect, useMemo, useState } from "react";
import api, { brl, dateBR } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Eye, LayoutGrid } from "lucide-react";
import { ProductOverviewDialog } from "@/components/ProductOverviewDialog";

const Kpi = ({ label, value, tone = "" }) => (
  <Card className="card-outline"><CardContent className="p-5">
    <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
    <div className={"kpi-num text-2xl mt-2 " + tone}>{value}</div>
  </CardContent></Card>
);

const coverageBadge = (r) => {
  if (r.type !== "product") return "—";
  if (r.coverage_days == null) return <Badge variant="outline">Sem giro</Badge>;
  if (r.coverage_days < 7) return <Badge variant="destructive">{r.coverage_days} dias</Badge>;
  if (r.coverage_days < 30) return <Badge className="bg-amber-500">{r.coverage_days} dias</Badge>;
  return <Badge className="bg-emerald-600">{r.coverage_days} dias</Badge>;
};

export default function ProductManagement() {
  const [data, setData] = useState({ days: 30, items: [] });
  const [days, setDays] = useState("30");
  const [q, setQ] = useState("");
  const [view, setView] = useState(null);

  useEffect(() => { api.get(`/products/management?days=${days}`).then((r) => setData(r.data)); }, [days]);

  const rows = useMemo(() => data.items.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()) || (r.category || "").toLowerCase().includes(q.toLowerCase())), [data, q]);
  const sum = (k) => rows.reduce((s, r) => s + (r[k] || 0), 0);

  return (
    <div className="space-y-6" data-testid="product-management-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Catálogo</div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight flex items-center gap-2"><LayoutGrid className="w-7 h-7" /> Gestão de Produtos</h1>
        <p className="text-sm text-slate-500 mt-1">Compras, estoque e vendas de cada produto em um só lugar.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi label="Valor em estoque" value={brl(sum("stock_value"))} />
        <Kpi label="Total comprado" value={brl(sum("purchased_amount"))} />
        <Kpi label="Total vendido" value={brl(sum("revenue"))} tone="text-emerald-600" />
        <Kpi label="Lucro bruto" value={brl(sum("profit"))} tone="text-blue-600" />
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="relative w-full sm:w-72">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Buscar produto ou categoria..." className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pm-search" />
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="w-48" data-testid="pm-days"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Giro: últimos 7 dias</SelectItem>
            <SelectItem value="30">Giro: últimos 30 dias</SelectItem>
            <SelectItem value="90">Giro: últimos 90 dias</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="card-outline">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Produto</TableHead><TableHead className="text-right">Estoque</TableHead>
              <TableHead className="text-right">Comprado</TableHead><TableHead className="text-right">Vendido</TableHead>
              <TableHead className="text-right">Vendas ({data.days}d)</TableHead><TableHead className="text-right">Faturado</TableHead>
              <TableHead className="text-right">Lucro</TableHead><TableHead className="text-right">Margem</TableHead>
              <TableHead>Cobertura</TableHead><TableHead>Última compra / venda</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`pm-row-${r.id}`}>
                  <TableCell className="font-medium">{r.name}<div className="text-[10px] text-slate-500">{r.type === "service" ? "Serviço" : (r.category || "")}</div></TableCell>
                  <TableCell className={"text-right kpi-num " + (r.type === "product" && r.stock <= r.min_stock ? "text-rose-600 font-bold" : "")}>
                    {r.type === "product" ? r.stock : "—"}{r.pending_qty > 0 && <div className="text-[10px] text-amber-600">+{r.pending_qty} a receber</div>}
                  </TableCell>
                  <TableCell className="text-right kpi-num">{r.purchased_qty}<div className="text-[10px] text-slate-500">{brl(r.purchased_amount)}</div></TableCell>
                  <TableCell className="text-right kpi-num">{r.sold_qty}</TableCell>
                  <TableCell className="text-right kpi-num">{r.sold_period}</TableCell>
                  <TableCell className="text-right kpi-num text-emerald-600">{brl(r.revenue)}</TableCell>
                  <TableCell className={"text-right kpi-num " + (r.profit < 0 ? "text-rose-600" : "")}>{brl(r.profit)}</TableCell>
                  <TableCell className="text-right kpi-num">{r.margin_pct}%</TableCell>
                  <TableCell>{coverageBadge(r)}</TableCell>
                  <TableCell className="text-xs text-slate-600">{r.last_purchase ? dateBR(r.last_purchase) : "—"}<br />{r.last_sale ? dateBR(r.last_sale) : "—"}</TableCell>
                  <TableCell><Button size="icon" variant="ghost" onClick={() => setView(r)} data-testid={`pm-view-${r.id}`}><Eye className="w-4 h-4" /></Button></TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && <TableRow><TableCell colSpan={11} className="text-center text-slate-400 py-8">Nenhum produto.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <p className="text-xs text-slate-500">Cobertura = quantos dias o estoque atual dura no ritmo de vendas do período. Margem calculada sobre o preço de venda.</p>

      <ProductOverviewDialog row={view} onClose={() => setView(null)} />
    </div>
  );
}
